import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPenToSquare, faSquarePlus, faSquareXmark } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { validateAllFormFields } from '../../../common/utils';
import { ApiService } from '../../services/api.service';

@Component({
    selector: 'app-partner-form',
    imports: [CommonModule, FontAwesomeModule, ReactiveFormsModule, FormsModule],
    templateUrl: './partner-form.component.html',
    styleUrl: './partner-form.component.css'
})
export class PartnerFormComponent {
  partnerForm!: FormGroup;
  isEdit = false;
  loading = false;
  saving = false;
  partnerId: number | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    library: FaIconLibrary
  ) {
    library.addIcons(faSquarePlus, faSquareXmark, faPenToSquare);
  }

  ngOnInit() {
    this.partnerForm = this.fb.group({
      name: [
        '',
        [Validators.required, Validators.pattern(/^[a-z0-9_]+$/)]
      ],
      display_name: ['', Validators.required],
      is_active: [true]
    });

    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (!id) {
        return;
      }
      this.isEdit = true;
      this.partnerId = Number(id);
      this.fetchPartner(this.partnerId);
    });
  }

  normalizeName() {
    const currentValue = this.partnerForm.get('name')?.value ?? '';
    const normalizedValue = currentValue
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    this.partnerForm.patchValue({ name: normalizedValue });
  }

  fetchPartner(id: number) {
    this.loading = true;
    this.apiService.getWebhookProvider(id).subscribe({
      next: (response: any) => {
        this.partnerForm.patchValue({
          name: response?.name ?? '',
          display_name: response?.display_name ?? '',
          is_active: response?.is_active ?? true
        });
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (err?.error?.detail) {
          this.toastr.error(err.error.detail, 'Error');
        } else if (err?.status !== 403 && err?.status !== 0) {
          this.toastr.error('Unable to load technology partner.', 'Error');
        }
        this.router.navigate(['admin/partners']);
      }
    });
  }

  onSubmit() {
    this.normalizeName();
    if (this.partnerForm.invalid) {
      validateAllFormFields(this.partnerForm);
      return;
    }

    const payload = {
      name: this.partnerForm.value.name.trim(),
      display_name: this.partnerForm.value.display_name.trim(),
      is_active: this.partnerForm.value.is_active
    };

    const request$ =
      this.isEdit && this.partnerId
        ? this.apiService.updateWebhookProvider(this.partnerId, payload)
        : this.apiService.createWebhookProvider(payload);

    this.saving = true;
    request$.subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(
          `Technology partner ${this.isEdit ? 'updated' : 'created'} successfully.`,
          'Success'
        );
        this.router.navigate(['admin/partners']);
      },
      error: (err) => {
        this.saving = false;
        if (err?.error?.name?.length) {
          err.error.name.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.error?.display_name?.length) {
          err.error.display_name.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.error?.detail) {
          this.toastr.error(err.error.detail, 'Error');
        } else if (err?.status !== 403 && err?.status !== 0) {
          this.toastr.error('Something went wrong!', 'Error');
        }
      }
    });
  }

  onCancel() {
    this.router.navigate(['admin/partners']);
  }
}
